import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { ResponsiveTabsList, ResponsiveTabsTrigger } from '@/components/admin/ResponsiveTabsComponents';
import { Building2, Settings, BarChart3, Package, Users, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { Organization } from '@/types/organization';
import { AnalyticsDashboard } from '@/components/admin/organization/AnalyticsDashboard';
import { OrganizationSettings } from '@/components/admin/organization/OrganizationSettings';
import { MemberManagement } from '@/components/admin/organization/MemberManagement';
import OrganizationParcelTypesManager from '@/components/admin/organization/OrganizationParcelTypesManager';
import ParcelTypesErrorBoundary from '@/components/admin/ParcelTypesErrorBoundary';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Save } from 'lucide-react';
import LogoUpload from '@/components/ui/logo-upload';
import { OrganizationDetailProvider } from '@/components/admin/organization/OrganizationDetailProvider';
import { useThemeApplicator } from '@/hooks/useThemeApplicator';
import { useThemeUtils } from '@/hooks/useThemeUtils';
import { toast } from '@/hooks/use-toast';

const OrganizationDetailAdmin = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [previewMode, setPreviewMode] = useState(true);
  
  const [orgData, setOrgData] = useState({
    subdomain: '',
    name: '',
    display_name: '',
    logo_url: '',
    is_active: true,
  });

  // Get global theme for fallback
  const { globalPrimaryColor } = useThemeUtils();

  // Apply theme preview for the organization being edited
  // This allows super admin to see the theme they're editing in real-time
  useThemeApplicator({
    organizationId: previewMode ? organization?.id : null,
    subdomain: organization?.subdomain,
    enabled: previewMode && !!organization,
    globalPrimaryColor,
  });

  console.log('[ORG-DETAIL-ADMIN] Preview mode:', previewMode, 'org:', organization?.name);

  useEffect(() => {
    fetchOrganization();
  }, [orgId]);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', orgId)
        .single();

      if (error) throw error;

      if (data) {
        setOrganization(data);
        setOrgData({
          subdomain: data.subdomain,
          name: data.name,
          display_name: data.display_name || '',
          logo_url: data.logo_url || '',
          is_active: data.is_active,
        });
      }
    } catch (error) {
      console.error('Error fetching organization:', error);
      toast({
        title: "Error",
        description: "Failed to load organization details",
        variant: "destructive",
      });
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (url: string | null) => {
    setOrgData(prev => ({ ...prev, logo_url: url || '' }));
  };

  const handleSaveBasicDetails = async () => {
    if (!organization) return;
    
    try {
      setSaving(true);
      const { error } = await supabase
        .from('organizations')
        .update({
          name: orgData.name,
          display_name: orgData.display_name,
          logo_url: orgData.logo_url,
          is_active: orgData.is_active,
        })
        .eq('id', organization.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Organization details updated successfully",
      });
      
      fetchOrganization();
    } catch (error) {
      console.error('Error updating organization:', error);
      toast({
        title: "Error",
        description: "Failed to update organization details",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const orgTabs = [
    { value: "details", label: "Details", icon: Building2 },
    { value: "settings", label: "Settings", icon: Settings },
    { value: "members", label: "Members", icon: Users },
    { value: "parcels", label: "Parcel Types", icon: Package },
    { value: "analytics", label: "Analytics", icon: BarChart3 },
  ];

  if (loading) {
    return (
      <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
        <div className="py-6 space-y-6 lg:py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading organization...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return null;
  }

  return (
    <OrganizationDetailProvider organization={organization}>
      <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
        <div className="py-6 space-y-6 lg:py-8">
        {/* Preview Mode Alert */}
        {activeTab === 'settings' && (
          <Alert className="border-primary/50 bg-primary/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {previewMode ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                <AlertDescription>
                  {previewMode ? (
                    <>
                      <strong>Live Preview Active:</strong> The header and UI above are showing{' '}
                      <strong>{organization?.display_name || organization?.name}</strong>'s theme.
                    </>
                  ) : (
                    <>
                      <strong>Preview Disabled:</strong> The header shows your platform theme.{' '}
                      <strong>Enable preview</strong> to see how this organization's theme will appear.
                    </>
                  )}
                </AlertDescription>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="preview-mode"
                  checked={previewMode}
                  onCheckedChange={setPreviewMode}
                />
                <Label htmlFor="preview-mode" className="cursor-pointer whitespace-nowrap">
                  Preview Theme
                </Label>
              </div>
            </div>
          </Alert>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center space-x-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/admin')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to System Admin
              </Button>
            </div>
            <div className="flex items-center space-x-3">
              {organization.logo_url && (
                <img 
                  src={organization.logo_url} 
                  alt={organization.name}
                  className="h-10 w-10 rounded-lg object-cover"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold">{organization.display_name || organization.name}</h1>
                <p className="text-muted-foreground">
                  {organization.subdomain}.parcelpilot.app
                  <Badge variant={organization.is_active ? "default" : "secondary"} className="ml-2">
                    {organization.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Mobile: Dropdown Navigation */}
          <div className="block sm:hidden">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full h-12 text-left">
                <div className="flex items-center space-x-2">
                  {(() => {
                    const currentTab = orgTabs.find(tab => tab.value === activeTab);
                    const Icon = currentTab?.icon;
                    return (
                      <>
                        {Icon && <Icon className="h-4 w-4" />}
                        <span>{currentTab?.label}</span>
                      </>
                    );
                  })()}
                </div>
              </SelectTrigger>
              <SelectContent className="z-50">
                {orgTabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <SelectItem key={tab.value} value={tab.value}>
                      <div className="flex items-center space-x-2">
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Desktop: Tab Navigation */}
          <div className="hidden sm:block">
            <ResponsiveTabsList className="grid-cols-5">
              <ResponsiveTabsTrigger
                value="details"
                icon={<Building2 className="h-4 w-4" />}
                label="Details"
              />
              <ResponsiveTabsTrigger
                value="settings"
                icon={<Settings className="h-4 w-4" />}
                label="Settings"
              />
              <ResponsiveTabsTrigger
                value="members"
                icon={<Users className="h-4 w-4" />}
                label="Members"
              />
              <ResponsiveTabsTrigger
                value="parcels"
                icon={<Package className="h-4 w-4" />}
                label="Parcel Types"
                mobileLabel="Parcels"
              />
              <ResponsiveTabsTrigger
                value="analytics"
                icon={<BarChart3 className="h-4 w-4" />}
                label="Analytics"
              />
            </ResponsiveTabsList>
          </div>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>
                  Manage organization's core details and branding
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="subdomain">Subdomain</Label>
                    <Input 
                      id="subdomain" 
                      value={orgData.subdomain} 
                      readOnly 
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      {organization.subdomain === 'swift' 
                        ? 'Cannot edit default platform organization subdomain'
                        : 'Contact support to change subdomain'
                      }
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="name">Organization Name</Label>
                    <Input 
                      id="name" 
                      value={orgData.name} 
                      onChange={(e) => setOrgData({ ...orgData, name: e.target.value })}
                      disabled={organization.subdomain === 'swift'}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="display-name">Display Name</Label>
                  <Input 
                    id="display-name" 
                    value={orgData.display_name} 
                    onChange={(e) => setOrgData({ ...orgData, display_name: e.target.value })}
                    placeholder="Friendly display name" 
                  />
                </div>

                <LogoUpload
                  onLogoChange={handleLogoChange}
                  currentLogo={orgData.logo_url}
                  organizationName={orgData.display_name || orgData.name}
                  disabled={saving}
                />

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="is-active">Organization Status</Label>
                    <p className="text-sm text-muted-foreground">
                      Control whether this organization is active and accessible
                    </p>
                  </div>
                  <Switch
                    id="is-active"
                    checked={orgData.is_active}
                    onCheckedChange={(checked) => setOrgData({ ...orgData, is_active: checked })}
                    disabled={organization.subdomain === 'swift'}
                  />
                </div>

                {organization.subdomain === 'swift' && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">
                      This is the default platform organization and cannot be fully edited or deactivated.
                    </p>
                  </div>
                )}

                <Button onClick={handleSaveBasicDetails} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Basic Details'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab - Reuse OrganizationSettings */}
          <TabsContent value="settings" className="space-y-6">
            <OrganizationSettings organizationId={organization.id} />
          </TabsContent>

          {/* Members Tab - Reuse MemberManagement */}
          <TabsContent value="members" className="space-y-6">
            <MemberManagement organizationId={organization.id} />
          </TabsContent>

          {/* Parcel Types Tab */}
          <TabsContent value="parcels" className="space-y-6">
            <ParcelTypesErrorBoundary>
              <OrganizationParcelTypesManager organizationId={organization.id} />
            </ParcelTypesErrorBoundary>
          </TabsContent>

          {/* Analytics Tab - Reuse AnalyticsDashboard */}
          <TabsContent value="analytics" className="space-y-6">
            <AnalyticsDashboard organizationId={organization.id} />
          </TabsContent>
        </Tabs>
        </div>
      </div>
    </OrganizationDetailProvider>
  );
};

export default OrganizationDetailAdmin;
